/**
 * In-app help content for AFC Group Connect — focused on roles & access (RBAC).
 * Each section groups related roles; each topic explains what a role can reach
 * and do. Icons are FontAwesome class strings (rendered as <i className={icon}/>),
 * matching the app's icon convention. Bodies are trusted HTML strings.
 *
 * The What's New section is generated from releaseNotes.js so the version shown
 * in Help is stated in exactly one place.
 */

import { APP_VERSION, APP_CHANNEL, releases } from './releaseNotes.js';

/** The "What changed" bullet list for one release entry. */
const highlightList = (release) => `
  <p style="margin-top:12px;"><strong>What changed:</strong></p>
  <ul>${release.highlights.map((h) => `<li>${h}</li>`).join('')}</ul>
`;

export const helpSections = [
  {
    id: 'overview',
    title: 'Overview',
    icon: 'fa-solid fa-circle-info',
    topics: [
      {
        id: 'how-access-works',
        title: 'How access works',
        body: `
          <p>AFC Group Connect is <strong>role-based</strong>: what you see and can do depends on your account's role and business title. The app only ever shows the workspaces you are allowed to use — links to pages outside your role are hidden, and typing their address sends you to a <span style="color:#f59e0b;">403 Not Authorized</span> page.</p>
          <p style="margin-top:12px;"><strong>Two things decide your access:</strong></p>
          <ul>
            <li><strong>Role</strong> — <code>connector</code>, internal staff (<code>admin</code>), Group Admin (<code>super_admin</code>), or <code>group_viewer</code>.</li>
            <li><strong>Title</strong> — for internal staff, the business title (Account Management, Branch Operations, Relationship Manager, Loans Officer, Compliance Officer, Finance User) narrows the workspace to just that function.</li>
          </ul>
          <p style="margin-top:12px;">A <strong>System Administrator</strong> is an internal staff account with no narrowing title — it has full <strong>per-company</strong> administration. The <strong>Group Admin</strong> is a separate, higher role that oversees connectors <strong>across the whole group of companies</strong> from the group console. The two are distinct: System Admin runs one company; Group Admin sees them all.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: if a page you expect is missing, your role likely doesn't include it — ask your System Administrator to check your title.</p>
        `,
      },
      {
        id: 'roles-at-a-glance',
        title: 'Roles at a glance',
        body: `
          <p>Each role owns a focused workspace:</p>
          <ul>
            <li><strong>Connector</strong> — submit and track referrals, view commissions, manage profile.</li>
            <li><strong>Account Management</strong> — screen incoming referrals and route them to a branch.</li>
            <li><strong>Branch Operations</strong> — confirm receipt and assign a Relationship Manager.</li>
            <li><strong>Relationship Manager</strong> — progress referral milestones and approved amounts.</li>
            <li><strong>Loans Officer</strong> — process commissions (override, reassign, finalize).</li>
            <li><strong>Compliance / Accreditation Officer</strong> — review and decide connector accreditation.</li>
            <li><strong>Finance User</strong> — export and release commission payouts.</li>
            <li><strong>System Administrator</strong> — full administration and configuration <em>for one company</em>.</li>
            <li><strong>Group Admin</strong> — group-wide performance and blacklist oversight <em>across all companies</em>.</li>
            <li><strong>Group Viewer</strong> — read-only access to the group console.</li>
          </ul>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#0b355b; font-weight:600; margin-bottom:8px;">Referral lifecycle</p>
            <p style="font-size:13px;">DRAFT &rarr; SUBMITTED &rarr; UNDER INTAKE &rarr; BRANCH ROUTED &rarr; ASSIGNED TO RM &rarr; RELEASED</p>
            <p style="font-size:13px; margin-top:6px;">Each stage is owned by a different role, so a referral hands off cleanly from the connector to Account Management, Branch Operations, then the Relationship Manager.</p>
          </div>
        `,
      },
    ],
  },
  {
    id: 'connector',
    title: 'Connector',
    icon: 'fa-solid fa-user',
    topics: [
      {
        id: 'connector-role',
        title: 'Connector',
        body: `
          <p>Connectors are external partners who refer borrowers. The connector workspace is a mobile-friendly PWA.</p>
          <p style="margin-top:12px;"><strong>You can access:</strong></p>
          <ul>
            <li><strong>Dashboard</strong> — your referrals, statuses, aging, and commission summary.</li>
            <li><strong>New Referral</strong> — the guided 3-step borrower referral wizard.</li>
            <li><strong>My Profile</strong> — personal details and payout information.</li>
            <li><strong>Onboarding</strong> — first-time KYC and accreditation.</li>
          </ul>
          <p style="margin-top:12px;"><strong>You can do:</strong></p>
          <ul>
            <li>Submit borrower referrals and continue saved <span style="color:#6b7280;">drafts</span>.</li>
            <li>Track each referral's stage, aging in days, and outcome.</li>
            <li>See <strong>Total Earned</strong>, <strong>Pending</strong>, and <strong>Last Payout</strong>, plus a per-record commission breakdown.</li>
            <li>Update payout details and complete mobile/email verification.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Note: creating referrals unlocks once your accreditation is approved. A blacklisted connector's access is revoked group-wide.</p>
        `,
      },
    ],
  },
  {
    id: 'operations',
    title: 'Referral Ops',
    icon: 'fa-solid fa-diagram-project',
    topics: [
      {
        id: 'account-management',
        title: 'Account Management',
        body: `
          <p>Account Management handles the first internal step after a connector submits a referral.</p>
          <p style="margin-top:12px;"><strong>Access:</strong> the <strong>Referrals</strong> queue (Account Management worklist).</p>
          <p style="margin-top:12px;"><strong>You can do:</strong></p>
          <ol>
            <li><strong>Screen for duplicates</strong> — run duplicate screening. This checks two things at once: prior referrals already in the system, and the <strong>ASearch Borrower Master List</strong> of existing Asialink customers.</li>
            <li><strong>Review existing-customer matches</strong> — if the borrower appears to already be an Asialink customer, the screening panel lists the possible match(es) with product, source, and current outstanding balance. These are <strong>advisory</strong> — nothing is auto-rejected; you decide the outcome.</li>
            <li><strong>Record the outcome</strong> — mark the referral <span style="color:#22c55e;">Cleared</span> or <span style="color:#ef4444;">Duplicate confirmed</span>.</li>
            <li><strong>Route to a branch</strong> — once cleared, assign the receiving branch and add remarks.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Branch routing only unlocks after duplicate screening is Cleared. Existing-customer details from the master list are visible to internal staff only and are never shown to connectors. If the master-list service is temporarily unavailable, internal duplicate screening still runs and the referral is never blocked.</p>
        `,
      },
      {
        id: 'branch-operations',
        title: 'Branch Operations',
        body: `
          <p>Branch Operations receives routed referrals and connects them to a Relationship Manager.</p>
          <p style="margin-top:12px;"><strong>Access:</strong> the <strong>Referrals</strong> queue (Branch Operations worklist).</p>
          <p style="margin-top:12px;"><strong>You can do:</strong></p>
          <ol>
            <li><strong>Confirm branch receipt</strong> — acknowledge the referral arrived at your branch (logged to the activity feed).</li>
            <li><strong>Assign a Relationship Manager</strong> — pick an RM. The list is <strong>scoped to your branch</strong>, so you only see RMs who serve it — no default is pre-selected.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Assignment stays disabled until branch receipt is confirmed and an RM is chosen.</p>
        `,
      },
      {
        id: 'relationship-manager',
        title: 'Relationship Manager',
        body: `
          <p>The Relationship Manager (RM) owns the referral through the lending pipeline.</p>
          <p style="margin-top:12px;"><strong>Access:</strong> the <strong>Referrals</strong> queue (RM pipeline).</p>
          <p style="margin-top:12px;"><strong>You can do:</strong></p>
          <ul>
            <li><strong>Progress milestones</strong> — Interview &rarr; Credit Investigation &rarr; Approved &rarr; Released.</li>
            <li><strong>Enter the approved loan amount</strong> when a referral reaches <strong>Approved</strong>.</li>
            <li><strong>Place or clear a dispute hold</strong>, with a required reason.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">RMs are mapped to one or more branches by a Group Admin, which controls where they can be assigned.</p>
        `,
      },
    ],
  },
  {
    id: 'finance',
    title: 'Commission & Finance',
    icon: 'fa-solid fa-coins',
    topics: [
      {
        id: 'loans-officer',
        title: 'Loans Officer',
        body: `
          <p>The Loans Officer processes commission records once referrals convert.</p>
          <p style="margin-top:12px;"><strong>Access:</strong> <strong>Commission Processing</strong>.</p>
          <p style="margin-top:12px;"><strong>You can do:</strong></p>
          <ul>
            <li><strong>Override</strong> the system-computed gross commission, with an audit reason.</li>
            <li><strong>Reassign</strong> a commission to another connector (blocked for suspended connectors).</li>
            <li><strong>Hold / release</strong> disputed records.</li>
            <li><strong>Finalize</strong> eligible commissions.</li>
          </ul>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#0b355b; font-weight:600; margin-bottom:8px;">Bank number privacy</p>
            <p style="font-size:13px;">Loans Officers see connector bank account numbers <strong>masked to the last 4 digits</strong>. Only Finance Users see the full number.</p>
          </div>
        `,
      },
      {
        id: 'finance-user',
        title: 'Finance User',
        body: `
          <p>The Finance User handles the final payout export and release.</p>
          <p style="margin-top:12px;"><strong>Access:</strong> <strong>Export &amp; Handoff</strong>.</p>
          <p style="margin-top:12px;"><strong>You can do:</strong></p>
          <ul>
            <li><strong>Compile</strong> finalized commissions into an export batch.</li>
            <li><strong>Release</strong> a batch with a handoff memo.</li>
            <li><strong>Download</strong> export files and review batch history.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Finance is the only role that sees <strong>full, unmasked</strong> bank account numbers — needed to disburse payouts.</p>
        `,
      },
    ],
  },
  {
    id: 'compliance',
    title: 'Compliance',
    icon: 'fa-solid fa-id-card',
    topics: [
      {
        id: 'compliance-officer',
        title: 'Compliance / Accreditation Officer',
        body: `
          <p>The Compliance / Accreditation Officer decides whether a connector is approved to refer.</p>
          <p style="margin-top:12px;"><strong>Access:</strong> <strong>Accreditation Review</strong>.</p>
          <p style="margin-top:12px;"><strong>You can do:</strong></p>
          <ul>
            <li><strong>Review</strong> a connector's submitted KYC documents (government ID, selfie) and profile.</li>
            <li><strong>Request corrections</strong> to a submitted profile on the connector's behalf.</li>
            <li><strong>Decide</strong> the accreditation — <span style="color:#22c55e;">Approve</span> or <span style="color:#ef4444;">Reject</span> — which unlocks or blocks the connector's ability to refer.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Accreditation is per-company: a connector approved at one company still applies separately at others.</p>
        `,
      },
    ],
  },
  {
    id: 'administration',
    title: 'Administration',
    icon: 'fa-solid fa-gear',
    topics: [
      {
        id: 'system-administrator',
        title: 'System Administrator',
        body: `
          <p>The System Administrator runs a <strong>single company</strong> and can reach every company workspace. This is the top admin per business — it does <em>not</em> include the group-wide console.</p>
          <p style="margin-top:12px;"><strong>Access &amp; actions:</strong></p>
          <ul>
            <li><strong>Admin Dashboard</strong> — security metrics, lockouts, audit activity.</li>
            <li><strong>Products & Comm</strong> — base rates and <strong>product campaigns</strong> with start/end dates.</li>
            <li><strong>User Directory</strong> — provision and manage internal staff.</li>
            <li><strong>Relationship Managers</strong> — map each RM to the branch(es) they serve.</li>
            <li><strong>Role Permissions</strong> — review and configure role access.</li>
            <li><strong>Notifications</strong> &amp; <strong>Branding</strong> — templates and company appearance.</li>
            <li><strong>Security Logs</strong> — audit trail and manual lockout release.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Group-wide oversight (cross-company performance, connector blacklist) belongs to the Group Admin — see the next topic.</p>
        `,
      },
      {
        id: 'group-admin',
        title: 'Group Admin & Group Viewer',
        body: `
          <p>The <strong>Group Admin</strong> is the group-wide super administrator. It oversees connectors <strong>across every company</strong> from a dedicated console — a level above the per-company System Administrator.</p>
          <p style="margin-top:12px;"><strong>Reach it</strong> at <code>/superadmin</code> (sign in with the Group Admin account).</p>
          <p style="margin-top:12px;"><strong>You can do:</strong></p>
          <ul>
            <li>See group KPIs, a status mix, monthly trend, and a <strong>connector leaderboard</strong> ranked across all companies.</li>
            <li>Open a connector's <strong>deep report</strong> — breakdown by company, loan product, and branch, plus commission per bucket.</li>
            <li><strong>Blacklist</strong> or un-blacklist a connector group-wide (the source of truth all companies enforce).</li>
            <li><strong>Manage viewers</strong> — grant read-only access to the console.</li>
          </ul>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#0b355b; font-weight:600; margin-bottom:8px;">Group Viewer</p>
            <p style="font-size:13px;">A <strong>Group Viewer</strong> sees the same console <strong>read-only</strong> — no blacklist actions and no viewer management. Ideal for executives who need visibility without control.</p>
          </div>
        `,
      },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    icon: 'fa-solid fa-circle-question',
    topics: [
      {
        id: 'why-cant-i-see',
        title: 'Why can’t I see a page?',
        body: `
          <p>Access is role-scoped. If a workspace isn't in your sidebar, your role doesn't include it, and opening its address directly redirects you to a <span style="color:#f59e0b;">403 Not Authorized</span> page.</p>
          <p style="margin-top:8px;">Your Group Admin can adjust your account's title to grant a different workspace. Connectors are limited to the connector workspace by design.</p>
        `,
      },
      {
        id: 'bank-numbers',
        title: 'Who can see bank account numbers?',
        body: `
          <p>Bank account numbers are <strong>masked to the last 4 digits</strong> for everyone except <strong>Finance Users</strong>, who need the full number to disburse payouts. This includes the Loans Officer and Group Admin views — they see the masked form.</p>
        `,
      },
      {
        id: 'blacklist',
        title: 'What does blacklisting do?',
        body: `
          <p>Blacklisting is a group-wide block managed by the Super Admin. A blacklisted connector loses access at <strong>every company</strong> — they cannot sign in to the workspace or submit referrals. The block is the group's source of truth; each company instance enforces it automatically.</p>
        `,
      },
      {
        id: 'reach-superadmin',
        title: 'How do I open the group console?',
        body: `
          <p>The group console at <code>/superadmin</code> is for the <strong>Group Admin</strong> and <strong>Group Viewer</strong> roles only — the per-company System Administrator does not have access. Sign in with the Group Admin account and you'll land on the console.</p>
          <p style="margin-top:8px;">If you're a System Administrator and need group-wide oversight, ask for a Group Admin or Group Viewer account.</p>
        `,
      },
    ],
  },
  {
    id: 'whats-new',
    title: "What's New",
    icon: 'fa-solid fa-rocket',
    topics: [
      {
        id: 'current-version',
        title: 'Current version',
        body: `
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px;">
            <p style="color:#0b355b; font-weight:600; margin-bottom:4px;">AFC Group Connect ${APP_VERSION} <span style="font-weight:500; color:#6b7280;">(${APP_CHANNEL})</span></p>
            <p style="font-size:13px; margin:0;">Released ${releases[0].date} — ${releases[0].title}.</p>
          </div>
          ${highlightList(releases[0])}
          <p style="margin-top:12px; font-size:13px; color:#9ca3af;">Earlier releases are listed under <strong>Release history</strong>. If something described here isn't in your workspace, the change may not apply to your role.</p>
        `,
      },
      {
        id: 'release-history',
        title: 'Release history',
        body: `
          <p>Every release of AFC Group Connect, newest first.</p>
          ${releases
            .map(
              (r) => `
          <div style="margin-top:16px;">
            <p style="color:#0b355b; font-weight:600; margin-bottom:2px;">${r.version} &mdash; ${r.title}</p>
            <p style="font-size:13px; color:#6b7280; margin-bottom:6px;">Released ${r.date}</p>
            <ul>${r.highlights.map((h) => `<li>${h}</li>`).join('')}</ul>
          </div>`
            )
            .join('')}
        `,
      },
    ],
  },
];

/**
 * Sections a connector may see (QA 8078). The staff guides — Referral Ops,
 * Finance, Compliance, Administration — document screens a connector has no
 * access to, so they were noise in the tab strip. Overview and the FAQ stay:
 * both exist largely to answer "why can't I see X", which is a connector's
 * question more than anyone's. What's New applies to everyone.
 */
const CONNECTOR_SECTIONS = ['overview', 'connector', 'faq', 'whats-new'];

/** The help sections visible to a given signed-in user. */
export function sectionsForUser(user) {
  if (user?.role !== 'connector') return helpSections;
  return helpSections.filter((s) => CONNECTOR_SECTIONS.includes(s.id));
}

/** Default help section for a given route pathname. */
export function getDefaultSection(pathname) {
  const p = pathname || '';
  if (p.includes('connector-dashboard') || p.includes('new-referral') || p.includes('connector-profile') || p.includes('connector-onboarding')) return 'connector';
  if (p.includes('referral-queue') || p.includes('referral-details')) return 'operations';
  if (p.includes('commission-processing') || p.includes('export-handoff')) return 'finance';
  if (p.includes('accreditation')) return 'compliance';
  if (p.includes('superadmin')) return 'administration';
  if (p.includes('admin-dashboard') || p.includes('user-administration') || p.includes('role-permission') || p.includes('relationship-managers') || p.includes('commission-configuration') || p.includes('branding') || p.includes('notification-management') || p.includes('audit-logs')) return 'administration';
  return 'overview';
}

/** Default help topic for a given route pathname. */
export function getDefaultTopic(pathname) {
  const p = pathname || '';
  if (p.includes('connector-')) return 'connector-role';
  if (p.includes('new-referral')) return 'connector-role';
  if (p.includes('commission-processing')) return 'loans-officer';
  if (p.includes('export-handoff')) return 'finance-user';
  if (p.includes('accreditation')) return 'compliance-officer';
  if (p.includes('superadmin')) return 'group-admin';
  if (p.includes('admin-dashboard') || p.includes('user-administration') || p.includes('role-permission')) return 'system-administrator';
  return 'how-access-works';
}

export default helpSections;
